import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  IconButton,
  Image,
  SimpleGrid,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { Avatar } from '../components/Avatar';
import { Check, FileText, Film, X } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getDefaultStore, useAtomValue, useSetAtom } from 'jotai';
import {
  addMessageAtom,
  currentUserAtom,
  fullScreenPageAtom,
  memberLabelsAtom,
  photoQualityAtom,
  shareDraftAtom,
} from '../store';
import type { Message } from '@twinden/shared';
import { useFamily, useLoadingState } from '../hooks';
import { LoadingPanel } from '../components/brand/TwindenLoader';
import { api, ApiError } from '../lib/api';
import {
  clearPendingShare,
  deleteOldShares,
  deleteShare,
  readShare,
  shareFileProblem,
  shareKind,
  shareMessage,
  type Share,
} from '../lib/shares';
import { sendFile } from '../lib/sendFiles';
import { queueMessage } from '../lib/outbox';
import { FamilySwitcher } from '../components/family/FamilySwitcher';
import { NoFamilySelected } from '../components/family/NoFamilySelected';
import type { ChatConversation } from '../components/chat/ConversationDialogs';
import { conversationName, formatBytes } from '../components/chat/messageUtils';
import { t as tNow, useT } from '../i18n';

// Share to Twinden (docs/share-to-twinden.md, D177–D182): the share sheet's
// photos, files and text arrive here from the service worker (sw-share.js),
// stored on this device. Pick a chat, add a message, Send: each file goes
// the composer's way (resized, sealed, retried) and the chat opens.

type Loaded =
  | { state: 'loading' }
  | { state: 'missing' }
  | { state: 'ready'; share: Share };

// Default group first (D10), then the most recent, as on home.
function sortChats(list: ChatConversation[]): ChatConversation[] {
  return [...list].sort((a, b) => {
    if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
    return (
      new Date(b.lastMessageAt ?? 0).getTime() -
      new Date(a.lastMessageAt ?? 0).getTime()
    );
  });
}

function SharedFiles({ files }: { files: File[] }) {
  const t = useT();
  const urls = useMemo(
    () =>
      files.map((file) =>
        shareKind(file) === 'image' || shareKind(file) === 'gif'
          ? URL.createObjectURL(file)
          : null
      ),
    [files]
  );
  useEffect(
    () => () => urls.forEach((url) => url && URL.revokeObjectURL(url)),
    [urls]
  );

  const photos = files
    .map((file, index) => ({ file, url: urls[index] }))
    .filter((entry) => entry.url);
  const others = files.filter((_, index) => !urls[index]);

  return (
    <VStack align="stretch" spacing={2}>
      {photos.length > 0 && (
        <SimpleGrid columns={photos.length === 1 ? 1 : 3} spacing={2}>
          {photos.map(({ file, url }) => (
            <Box
              key={url}
              position="relative"
              borderRadius="14px"
              overflow="hidden"
              bg="surface.subtle"
              aspectRatio={photos.length === 1 ? undefined : 1}
            >
              <Image
                src={url ?? undefined}
                alt={file.name}
                w="full"
                h={photos.length === 1 ? 'auto' : 'full'}
                maxH={photos.length === 1 ? '220px' : undefined}
                objectFit="cover"
              />
            </Box>
          ))}
        </SimpleGrid>
      )}
      {others.map((file, index) => {
        const problem = shareFileProblem(file);
        const Icon = shareKind(file) === 'video' ? Film : FileText;
        return (
          <HStack
            key={`${file.name}-${index}`}
            spacing={3}
            p="10px"
            bg="surface.subtle"
            borderRadius="14px"
          >
            <Flex
              w="40px"
              h="40px"
              borderRadius="12px"
              bg="accent.tint"
              color="accent"
              align="center"
              justify="center"
              flexShrink={0}
            >
              <Icon size={20} aria-hidden="true" />
            </Flex>
            <Box minW={0} flex="1">
              <Text fontWeight={700} fontSize="15px" noOfLines={1}>
                {file.name}
              </Text>
              <Text fontSize="13px" color={problem ? 'red.500' : 'muted'}>
                {problem === 'type'
                  ? t('chat.share.cantSendType')
                  : problem === 'size'
                    ? t('chat.share.tooBig')
                    : formatBytes(file.size)}
              </Text>
            </Box>
          </HStack>
        );
      })}
    </VStack>
  );
}

export function SharePage() {
  const [searchParams] = useSearchParams();
  const shareId = searchParams.get('id');
  const failed = searchParams.get('failed');
  const navigate = useNavigate();
  const t = useT();
  const { currentFamilyId } = useFamily();
  const currentUser = useAtomValue(currentUserAtom);
  const memberLabels = useAtomValue(memberLabelsAtom);
  const photoQuality = useAtomValue(photoQualityAtom);
  const setShareDraft = useSetAtom(shareDraftAtom);

  const [loaded, setLoaded] = useState<Loaded>({ state: 'loading' });
  const [chats, setChats] = useState<ChatConversation[] | null>(null);
  const [chatsError, setChatsError] = useState<string | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const sentRef = useRef({ text: false, files: new Set<number>() });
  const toast = useToast();

  const setFullScreen = useSetAtom(fullScreenPageAtom);
  useEffect(() => {
    setFullScreen(true);
    return () => setFullScreen(false);
  }, [setFullScreen]);

  // The share, read once; old ones are cleared on the way (D179).
  useEffect(() => {
    clearPendingShare();
    let live = true;
    void (async () => {
      await deleteOldShares();
      const share = shareId ? await readShare(shareId).catch(() => null) : null;
      if (!live) return;
      if (!share) {
        setLoaded({ state: 'missing' });
        return;
      }
      setLoaded({ state: 'ready', share });
      setMessage(shareMessage(share));
    })();
    return () => {
      live = false;
    };
  }, [shareId]);

  // The current family's chats; the switcher re-scopes them (D8).
  useEffect(() => {
    if (!currentFamilyId) return;
    let live = true;
    setChats(null);
    setChosen(null);
    setChatsError(null);
    api
      .getConversations(currentFamilyId)
      .then((response) => {
        if (live) setChats(sortChats(response.data as ChatConversation[]));
      })
      .catch((err: unknown) => {
        if (!live) return;
        setChatsError(
          err instanceof ApiError ? err.message : tNow('common.somethingWrong')
        );
      });
    return () => {
      live = false;
    };
  }, [currentFamilyId]);

  const share = loaded.state === 'ready' ? loaded.share : null;
  // The mark while the share is read and while the chats load (M66, D248).
  const shareState = useLoadingState({ loading: loaded.state === 'loading' });
  const chatsState = useLoadingState({
    loading: chats === null && !chatsError,
    hasData: chats !== null,
  });
  const sendable = share
    ? share.files.filter((file) => shareFileProblem(file) === null)
    : [];
  const textOnly = share !== null && share.files.length === 0;

  const close = () => navigate('/', { replace: true });

  const cancel = async () => {
    if (shareId) await deleteShare(shareId);
    close();
  };

  const chatLabel = (chat: ChatConversation) =>
    chat.isDefault
      ? t('chat.header.everyone')
      : conversationName(chat, currentUser?.id, memberLabels);

  const send = async () => {
    if (!share || !chosen) return;
    const conversationId = chosen;
    const text = message.trim();

    // Text or a link alone waits in the chat's composer (D180).
    if (textOnly) {
      if (text) setShareDraft({ conversationId, text });
      await deleteShare(share.id);
      navigate(`/?conversation=${conversationId}`, { replace: true });
      return;
    }

    setSending({ done: 0, total: sendable.length });
    // The message first, through the outbox (sealed, retried, D31, D66);
    // then each file the composer's way (D181). What has gone is remembered,
    // so nothing is ever sent twice.
    const done = sentRef.current;
    if (text && !done.text) {
      queueMessage({ conversationId, content: text });
      done.text = true;
    }
    const store = getDefaultStore();
    const onSent = (sent: unknown) =>
      store.set(addMessageAtom, {
        conversationId,
        message: sent as Message,
      });
    const skipped: string[] = [];
    for (const [index, file] of sendable.entries()) {
      if (!done.files.has(index)) {
        try {
          await sendFile(conversationId, file, { photoQuality, onSent });
        } catch {
          // A photo or video this browser can't read (HEIC in Chrome, say)
          // still goes, as a file (D181).
          try {
            await sendFile(conversationId, file, {
              photoQuality,
              onSent,
              as: 'file',
            });
          } catch {
            skipped.push(file.name);
          }
        }
        done.files.add(index);
      }
      setSending({ done: index + 1, total: sendable.length });
    }
    if (skipped.length > 0) {
      toast({
        title: t('chat.share.skipped', { names: skipped.join(', ') }),
        status: 'warning',
      });
    }
    await deleteShare(share.id);
    navigate(`/?conversation=${conversationId}`, { replace: true });
  };

  if (!currentFamilyId) return <NoFamilySelected />;

  const header = (
    <HStack
      spacing={1}
      px={2}
      pt={{ base: 'max(env(safe-area-inset-top), 0.5rem)', md: 2 }}
      minH="56px"
      flexShrink={0}
    >
      <IconButton
        aria-label={t('chat.share.cancel')}
        icon={<X size={24} />}
        variant="ghost"
        colorScheme="gray"
        onClick={() => void cancel()}
        isDisabled={sending !== null}
      />
      <Box flex="1" minW={0}>
        <FamilySwitcher />
      </Box>
    </HStack>
  );

  if (loaded.state === 'missing') {
    return (
      <Flex direction="column" h="full" maxW="720px" mx="auto" w="full">
        {header}
        <VStack align="stretch" spacing={3} px={4} pt={6}>
          <Heading as="h1" fontSize="28px" lineHeight={1.1}>
            {t('chat.share.goneTitle')}
          </Heading>
          <Text color="muted" fontSize="15px">
            {failed === 'size'
              ? t('chat.share.tooBigBody')
              : failed
                ? t('chat.share.failedBody')
                : t('chat.share.goneBody')}
          </Text>
          <Button alignSelf="flex-start" onClick={close}>
            {t('chat.share.toChats')}
          </Button>
        </VStack>
      </Flex>
    );
  }

  const chosenChat = chats?.find((chat) => chat.id === chosen) ?? null;
  const canSend =
    share !== null &&
    chosenChat !== null &&
    sending === null &&
    (textOnly ? true : sendable.length > 0 || message.trim().length > 0);

  return (
    <Flex direction="column" h="full" maxW="720px" mx="auto" w="full">
      {header}

      <Box
        flex="1"
        minH={0}
        overflowY="auto"
        px={4}
        pb={4}
        sx={{ overscrollBehaviorY: 'contain' }}
      >
        <VStack align="stretch" spacing={1} pb={4}>
          <Heading as="h1" fontSize="32px" lineHeight={1}>
            {t('chat.share.title')}
          </Heading>
          <Text fontSize="14px" color="muted">
            {share
              ? textOnly
                ? t('chat.share.subtitleText')
                : t('chat.share.subtitleFiles', { n: share.files.length })
              : t('chat.share.loading')}
          </Text>
        </VStack>

        {share ? (
          <VStack
            align="stretch"
            spacing={3}
            p="14px"
            bg="surface"
            borderWidth="1px"
            borderColor="line"
            borderRadius="card"
            mb={4}
          >
            {share.files.length > 0 && <SharedFiles files={share.files} />}
            <Textarea
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder={t('chat.share.messagePlaceholder')}
              aria-label={t('chat.share.messageLabel')}
              rows={textOnly ? 4 : 2}
              resize="none"
              borderRadius="14px"
              bg="surface.subtle"
              borderColor="line"
              fontSize="16px"
            />
          </VStack>
        ) : (
          <LoadingPanel state={shareState} mb={4} />
        )}

        <Text
          fontSize="13px"
          fontWeight={700}
          color="muted"
          textTransform="uppercase"
          letterSpacing="0.06em"
          mb={2}
        >
          {t('chat.share.chooseChat')}
        </Text>
        {chatsError && (
          <Alert status="error" borderRadius="xl" mb={3}>
            <AlertIcon />
            {chatsError}
          </Alert>
        )}
        {chats === null || chatsState.show ? (
          <LoadingPanel state={chatsState} size="inline" />
        ) : (
          <VStack align="stretch" spacing="8px" role="radiogroup">
            {(chats ?? []).map((chat) => {
              const label = chatLabel(chat);
              const selected = chosen === chat.id;
              const other =
                chat.type === 'direct'
                  ? chat.participants.find((p) => p.id !== currentUser?.id)
                  : undefined;
              return (
                <HStack
                  key={chat.id}
                  as="button"
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  spacing={3}
                  p="12px"
                  bg={selected ? 'accent.tint' : 'surface'}
                  borderWidth={selected ? '2px' : '1px'}
                  borderColor={selected ? 'accent' : 'line'}
                  borderRadius="card"
                  textAlign="left"
                  onClick={() => setChosen(chat.id)}
                  disabled={sending !== null}
                >
                  <Avatar
                    name={label}
                    src={chat.avatarUrl ?? other?.avatarUrl ?? undefined}
                    // The group's own picture, or the other person's (D292).
                    ownerId={chat.avatarUrl ? chat.id : other?.id}
                    familyId={currentFamilyId}
                    size="sm"
                  />
                  <Box flex="1" minW={0}>
                    <Text fontWeight={700} fontSize="16px" noOfLines={1}>
                      {label}
                    </Text>
                    {chat.type === 'group' && (
                      <Text fontSize="13px" color="muted">
                        {t('chat.header.people', {
                          n: chat.participants.length,
                        })}
                      </Text>
                    )}
                  </Box>
                  {selected && (
                    <Flex
                      w="24px"
                      h="24px"
                      borderRadius="full"
                      bg="accent"
                      color="white"
                      align="center"
                      justify="center"
                      aria-hidden="true"
                    >
                      <Check size={16} />
                    </Flex>
                  )}
                </HStack>
              );
            })}
          </VStack>
        )}
      </Box>

      <Box
        px={4}
        pt={3}
        pb="max(env(safe-area-inset-bottom), 0.75rem)"
        borderTopWidth="1px"
        borderColor="line"
        bg="surface"
        flexShrink={0}
      >
        <Button
          w="full"
          size="lg"
          isDisabled={!canSend}
          isLoading={sending !== null}
          loadingText={
            sending
              ? t('chat.share.preparing', {
                  done: Math.min(sending.done + 1, sending.total),
                  total: sending.total,
                })
              : undefined
          }
          onClick={() => void send()}
        >
          {chosenChat
            ? t(textOnly ? 'chat.share.writeIn' : 'chat.share.sendTo', {
                name: chatLabel(chosenChat),
              })
            : t('chat.share.pickFirst')}
        </Button>
      </Box>
    </Flex>
  );
}
